import React, { FC } from 'react';

import { Link } from 'react-router-dom';
import toLower from 'lodash/toLower';
import { LocationDescriptor } from 'history';

import RoutingUtils from '../utils/utils-routing';
import {
    PRODUCT_CLASS_LANGUAGE_LEARNING,
    PRODUCT_CLASS_LOTE,
    PRODUCT_CLASS_STANDARD,
    ProductClass
} from '../../store/constants';
import MetadataUtils from '../utils/utils-metadata';
import { ProductMetaData } from '../../../models/Product';
import { useLanguage } from '../../contexts/language';
import { useTranslation } from 'react-i18next';

type Props = {
    readonly product?: ProductMetaData;
    readonly label?: string;
    readonly withoutLink?: boolean;
    readonly loanFormatKey?: string | null;
};

export const ProductLanguage: FC<Props> = ({ product = {}, withoutLink = false, label, loanFormatKey }) => {
    const { productClass } = product;
    const language = useLanguage();
    const filterLanguage = product.language;
    const pathLanguage = language;

    const { t } = useTranslation('filters');

    if (!language) {
        return null;
    }

    if (withoutLink) {
        return (
            <p>
                {t(`language.${label}`, {
                    defaultValue: label
                })}
            </p>
        );
    }

    const linkTo = setLanguageLink(productClass, pathLanguage, filterLanguage, loanFormatKey);

    return (
        <p>
            {linkTo ? (
                <Link to={linkTo}>
                    {t(`language.${label}`, {
                        defaultValue: label
                    })}
                </Link>
            ) : (
                <span>
                    {t(`language.${label}`, {
                        defaultValue: label
                    })}
                </span>
            )}
        </p>
    );
};

export default ProductLanguage;

function setLanguageLink(
    productClass: ProductClass | undefined,
    pathLanguage: string,
    filterLanguage: string | null | undefined,
    loanFormatKey: string | null | undefined
): LocationDescriptor {
    const isCatalogAllProductsLink = productClass === PRODUCT_CLASS_STANDARD;
    const isLoteProductLink = productClass === PRODUCT_CLASS_LOTE;
    const isLanguageLearningProductLink = productClass === PRODUCT_CLASS_LANGUAGE_LEARNING;

    if (isCatalogAllProductsLink) {
        const catalogueFilter = { language: { include: [filterLanguage] } };
        return RoutingUtils.catalogueLocationDescriptorString(loanFormatKey, catalogueFilter);
    }

    if (isLoteProductLink) {
        const lowerLoanFormat = toLower(loanFormatKey ?? 'all');
        // PORTAL-3993 use all products link instead of featured link
        return RoutingUtils.foreignLanguageAllProductsPageLocationDescriptor(filterLanguage, lowerLoanFormat);
    }

    if (isLanguageLearningProductLink) {
        const filter = {
            language: { include: [filterLanguage] }
        };
        return MetadataUtils.createMetaDataLink(productClass, filter, { loanFormatKey, language: pathLanguage });
    }

    return '';
}
